import {
    collection,
    CollectionReference,
    DocumentData,
    Timestamp,
    getCountFromServer,
    doc,
    addDoc,
    getDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    query,
    orderBy,
    limit,
    where,
    increment,
    arrayUnion
} from "firebase/firestore";
import { fs } from "../firebase.ts";
import { getDeviceID } from "../components/global.ts";

// stamps created by hb for testing/demo as v0
interface Stamp {
    id?: string; // doc id => b.333.eco/x/id
    date: Timestamp; // created by admin only
    type: "silver" | "gold" | "color"; // default: silver

    index?: number; // computed // later do auto increment for paging
    isPrinted?: boolean; // default: false

    // undefined = not linked yet
    uid?: string; // linker: must be logged in to link
    chest?: string; // cloud chest id

    // TODO: later add more fields for all stamp types: see Chests
    //latitude: number;
    //longitude: number;
    //address: string; // human readable address

    // stats: from here
    digs?: number; // total digs
    diggers?: string[]; // device id of diggers
    miners?: string[]; // uid of miners

    // stats: b app
    previews?: number;
    crawlers?: Array<string>;
    scans?: number; // total scans
    scanners?: string[]; // device id of scanners
}

// temp for demo
export class Stamps {
    private collection: CollectionReference<DocumentData>;
    public total = 0;

    constructor() {
        this.collection = collection(fs, "stamps");
    }

    async count(): Promise<number> {
        const snapshot = await getCountFromServer(this.collection);
        return snapshot.data().count;
    }

    async isStamp(uid: string, id: string): Promise<boolean> {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("chest", "==", id),
            limit(1)
        );
        const snap = await getDocs(q);
        return !snap.empty;
    }

    async stampCountTotal(id: string): Promise<number> {
        const q = query(this.collection, where("chest", "==", id));
        const snapshot = await getCountFromServer(q);
        return snapshot.data().count;
    }

    // anyone can link any cloud b-chest as many times as they like // in case of lost or damage
    async stampCount(uid: string, id: string): Promise<number> {
        const q = query(
            this.collection,
            where("uid", "==", uid),
            where("chest", "==", id)
        );
        const snapshot = await getCountFromServer(q);
        return snapshot.data().count;
    }

    async isMinted(id: string): Promise<boolean> {
        const stampRef = doc(fs, "stamps", id);
        const stampSnap = await getDoc(stampRef);
        return stampSnap.exists() && stampSnap.data().chest;
    }

    async get(id: string): Promise<Stamp | null> {
        const stampRef = doc(fs, "stamps", id);
        const stampSnap = await getDoc(stampRef);
        return stampSnap.exists()
            ? ({ id: stampSnap.id, ...stampSnap.data() } as Stamp)
            : null;
    }

    // admin only
    async create(type: Stamp["type"] = "silver"): Promise<Stamp> {
        const stamp: Stamp = {
            date: Timestamp.now(),
            type
        };
        const docRef = await addDoc(this.collection, stamp);

        return {
            id: docRef.id,
            ...stamp
        };
    }

    // not used
    async remove(id: string) {
        const docRef = doc(fs, "stamps", id);
        await deleteDoc(docRef);
    }

    async getAll(): Promise<Stamp[]> {
        const q = query(
            this.collection,
            orderBy("date", "asc") // or desc
            //limit(100)
        );
        const result = await getDocs(q);
        this.total = result.size;

        const stamps: Stamp[] = [];
        result.docs.forEach((doc, index) => {
            const stamp = doc.data() as Stamp;
            stamps.push({
                id: doc.id,
                index: index + 1,
                ...stamp
            } as Stamp);
        });

        return stamps.reverse();
    }

    // logged in users only
    async link(id: string, uid: string, chest: string, updateChest = false) {
        const docRef = doc(fs, "stamps", id);
        await updateDoc(docRef, {
            uid,
            chest
        });
        // if (updateChest) {
        //     const docRef = doc(fs, "chests", chest);
        //     await updateDoc(docRef, {
        //         isStamp: true
        //     });
        // }
    }

    // admin only
    async updateStatus(id: string, isPrinted: boolean) {
        const docRef = doc(fs, "stamps", id);
        await updateDoc(docRef, {
            isPrinted
        });
    }

    // anyone
    async addMiner(uid: string, id: string) {
        const docRef = doc(fs, "stamps", id);
        uid
            ? await updateDoc(docRef, {
                  digs: increment(1),
                  miners: arrayUnion(uid)
              })
            : await updateDoc(docRef, {
                  digs: increment(1),
                  diggers: arrayUnion(getDeviceID())
              });
    }
}
